import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Loader2, Hash, Search } from "lucide-react";

interface CreateChannelDialogProps {
  trigger?: React.ReactNode;
  onChannelCreated?: (channelId: string) => void;
}

interface TeamMember {
  id: string;
  full_name: string | null;
  email: string | null;
}

export const CreateChannelDialog = ({
  trigger,
  onChannelCreated,
}: CreateChannelDialogProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const queryClient = useQueryClient();

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team-members-for-channel"],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      const currentUserId = session?.session?.user?.id;

      const { data: roles } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["admin", "consultant", "marketing_and_sales", "finance", "reporting"]);

      if (!roles || roles.length === 0) return [];

      const userIds = [...new Set(roles.map((r) => r.user_id))];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", userIds)
        .neq("id", currentUserId || "")
        .order("full_name");

      return (profiles || []) as TeamMember[];
    },
    enabled: open,
  });

  const filteredMembers = teamMembers.filter((member) => {
    const searchLower = memberSearch.toLowerCase();
    return (
      member.full_name?.toLowerCase().includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower)
    );
  });

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const { mutate: createChannel, isPending } = useMutation({
    mutationFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error("Not authenticated");

      const formattedName = name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

      const { data, error } = await supabase
        .from("message_channels")
        .insert({
          name: `#${formattedName}`,
          channel_type: "custom",
          created_by: session.session.user.id,
        })
        .select()
        .single();

      if (error) throw error;

      const memberInserts = [
        { channel_id: data.id, user_id: session.session.user.id },
        ...selectedMembers.map((userId) => ({
          channel_id: data.id,
          user_id: userId,
        })),
      ];

      const { error: memberError } = await supabase
        .from("channel_members")
        .insert(memberInserts);

      if (memberError) {
        console.error("Error adding channel members:", memberError);
      }

      return data;
    },
    onSuccess: (data) => {
      toast.success("Channel created successfully!");
      queryClient.invalidateQueries({ queryKey: ["message-channels"] });
      queryClient.invalidateQueries({ queryKey: ["custom-channels"] });
      setOpen(false);
      setName("");
      setDescription("");
      setIsPrivate(false);
      setSelectedMembers([]);
      setMemberSearch("");
      onChannelCreated?.(data.id);
    },
    onError: (error) => {
      console.error("Error creating channel:", error);
      toast.error("Failed to create channel");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Channel name is required");
      return;
    }
    createChannel();
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setName("");
      setDescription("");
      setIsPrivate(false);
      setSelectedMembers([]);
      setMemberSearch("");
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)} style={{ cursor: "pointer" }}>
        {trigger || (
          <Button size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-1" />
            Create Channel
          </Button>
        )}
      </span>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="w-[480px] sm:w-[480px] flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Create a Channel
            </SheetTitle>
            <SheetDescription>
              Channels are where your team communicates. They&apos;re best when
              organized around a topic — #marketing, for example.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="space-y-4 py-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="channel-name">Name</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    #
                  </span>
                  <Input
                    id="channel-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. marketing"
                    className="pl-7"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Names must be lowercase, without spaces or periods.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="channel-description">
                  Description{" "}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="channel-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this channel about?"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Add Members</Label>
                <div className="border rounded-lg">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search team members..."
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        className="h-8 pl-8"
                      />
                    </div>
                  </div>
                  <ScrollArea className="h-[150px]">
                    <div className="p-2 space-y-1">
                      {filteredMembers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {memberSearch
                            ? "No members found"
                            : "No team members available"}
                        </p>
                      ) : (
                        filteredMembers.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer"
                            onClick={() => toggleMember(member.id)}
                          >
                            <Checkbox
                              checked={selectedMembers.includes(member.id)}
                              onCheckedChange={() => {}}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {getInitials(member.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">
                              {member.full_name || member.email}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedMembers.length} member(s) selected (+ you)
                </p>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="private-channel">Make private</Label>
                  <p className="text-xs text-muted-foreground">
                    When a channel is private, it can only be viewed or joined by
                    invitation.
                  </p>
                </div>
                <Switch
                  id="private-channel"
                  checked={isPrivate}
                  onCheckedChange={setIsPrivate}
                />
              </div>
            </div>

            <SheetFooter className="pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !name.trim()}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Channel
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
};
